/**
 * Element editability checker.
 * Determines which elements can be text-edited by the user.
 */

import { CONFIG } from './config'
import { log } from './utils'

const NON_EDITABLE_TAGS = new Set([
  'CANVAS', 'SVG', 'VIDEO', 'AUDIO', 'IMG',
  'IFRAME', 'OBJECT', 'EMBED',
  'A',
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'LINK', 'META',
  'BR', 'HR', 'WBR',
])

const MATH_CLASSES = [
  'MathJax', 'mjx-math', 'mjx-chtml', 'mjx-mrow',
  'katex', 'katex-html', 'katex-mathml',
]

const CHART_CLASS_PREFIXES = [
  'echarts', 'highcharts', 'chartjs',
  'd3-', 'plotly', 'vis-',
]

function isMathElement(element: Element): boolean {
  const tag = element.tagName.toLowerCase()
  if (tag === 'math') return true
  try {
    if (element.closest?.('math')) return true
  } catch {
    let p = element.parentElement
    while (p && p !== document.body) {
      if (p.tagName.toLowerCase() === 'math') return true
      p = p.parentElement
    }
  }

  if (MATH_CLASSES.some((cls) => element.classList.contains(cls))) return true
  if (element.hasAttribute('data-mathml') || element.hasAttribute('data-latex')) return true

  const parent = element.parentElement
  if (parent && MATH_CLASSES.some((cls) => parent.classList.contains(cls))) return true

  return false
}

function isChartElement(element: Element): boolean {
  return CHART_CLASS_PREFIXES.some((prefix) => Array.from(element.classList).some((cls) => cls.startsWith(prefix)))
}

function hasEditableText(element: Element): boolean {
  for (const child of Array.from(element.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent || ''
      if (text.trim().length > 0) return true
    } else if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'BR') {
      return true
    }
  }
  return false
}

export function generateUniqueId(): string {
  return `musk-edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export function isTextEditable(element: Element): boolean {
  if (NON_EDITABLE_TAGS.has(element.tagName)) return false
  if ((element as HTMLElement).shadowRoot) return false
  if (element.tagName.includes('-')) return false
  if (isMathElement(element)) return false
  if (isChartElement(element)) return false
  const contentEditable = element.getAttribute('contenteditable')
  if (contentEditable === 'false') return false
  if (!hasEditableText(element)) return false
  return true
}

export function getEditableStatus(element: Element): boolean {
  return element.getAttribute(CONFIG.EDITABLE_ATTR) === 'true'
}

export function markAllElements(root: Element = document.body): {
  editableCount: number,
  nonEditableCount: number,
  total: number,
} {
  const allElements = root.querySelectorAll('*')
  let editableCount = 0
  let nonEditableCount = 0

  allElements.forEach((element) => {
    const editable = isTextEditable(element)
    element.setAttribute(CONFIG.EDITABLE_ATTR, String(editable))

    if (!element.hasAttribute('data-musk-element-id')) {
      element.setAttribute('data-musk-element-id', generateUniqueId())
    }

    if (editable) editableCount++
    else nonEditableCount++
  })

  log('元素标记完成', {
    editableCount,
    nonEditableCount,
    total: allElements.length,
  })

  return { editableCount, nonEditableCount, total: allElements.length }
}

/**
 * 仅标记尚未标记的新 DOM 元素（例如预览模式下弹窗动态创建的元素）。
 * 已标记的元素不会被重新判断，避免覆盖编辑状态。
 */
export function markNewElements(root: Element = document.body): number {
  const unmarked = root.querySelectorAll(':not([data-musk-element-id])')
  let count = 0
  unmarked.forEach((element) => {
    if (element === root) return
    const editable = isTextEditable(element)
    element.setAttribute(CONFIG.EDITABLE_ATTR, String(editable))
    element.setAttribute('data-musk-element-id', generateUniqueId())
    if (editable && element.tagName === 'BUTTON') {
      element.setAttribute('tabindex', '0')
    }
    count++
  })
  if (count > 0) {
    log('补标记新元素', { count })
  }
  return count
}
